import React, { ReactNode, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  postRowQueries,
  postScheduledQueryKey,
  remove,
} from '../../../api/posts'
import { societyQueries } from '../../../api/societies'
import { PostRow } from '../../../generated/api/post-row'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useModal } from '../../../hooks/useModal'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../helpers/society'
import {
  formatDateWithFormat,
  formatDistanceToNow,
  getDateTime,
} from '../../../helpers/date'
import { removeMarkdown, replaceHtml } from '../../../helpers/string'
import { Icon, IconChoices } from '../../common/Icon'
import { Button, ButtonVariant } from '../../common/Button'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { ToastType } from '../../common/Toast/toast-type'
import { CreateEditPostModal } from '../CreateEditPostModal'
import { removePostRow } from '../postRowCache'
import { useTogglePin } from '../useTogglePin'
import { theme } from '../../../theme/theme'

const plainText = (message: string): string =>
  removeMarkdown(
    replaceHtml(
      message
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    )
  )

const ScheduledPostItem = ({
  row,
  onEdit,
}: {
  row: PostRow
  onEdit: () => void
}): ReactNode => {
  const { translate } = useAppTranslation()
  const queryClient = useQueryClient()
  const { setToastNotification } = useToastNotifications()
  const togglePin = useTogglePin(row)
  const { actions, isPinned } = row
  const publishAt = new Date(row.createdAt)

  const {
    show: showDeleteModal,
    open: openDeleteModal,
    close: closeDeleteModal,
  } = useModal()

  const { mutate: mutateRemovePost } = useMutation({
    mutationFn: async () => remove(row.id),
    meta: { skipGlobalInvalidate: true },
    onSuccess: () => {
      removePostRow(queryClient, row.id)
      setToastNotification(
        ToastType.SUCCESS,
        translate('post.flashMessage.deletePostSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('post.flashMessage.deletePostFailure')
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: postScheduledQueryKey })
    },
  })

  return (
    <li className="flex items-center gap-4 px-4 py-3">
      <div className="flex w-12 shrink-0 flex-col items-center">
        <span
          className="uppercase text-blue"
          style={theme.textVariants.fineprintBold}
        >
          {formatDateWithFormat(publishAt, 'MMM').replace('.', '').slice(0, 3)}
        </span>
        <span style={theme.textVariants.h3}>
          {formatDateWithFormat(publishAt, 'dd')}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="truncate" style={theme.textVariants.baseBold}>
          {plainText(row.message)}
        </p>
        <p className="text-neutral-40" style={theme.textVariants.caption}>
          {translate('scheduledPosts.publishInfo', {
            time: getDateTime(publishAt),
            relative: formatDistanceToNow(publishAt),
          })}
        </p>
      </div>
      {actions.canPin && (
        <Button
          variant={ButtonVariant.TEXT}
          size="no-padding"
          label={translate(
            isPinned ? 'common.actions.unpin' : 'common.actions.pin'
          )}
          onClick={() => togglePin(!isPinned)}
        />
      )}
      {actions.canEdit && (
        <Button
          variant={ButtonVariant.TEXT}
          size="no-padding"
          label={translate('scheduledPosts.edit')}
          onClick={onEdit}
        />
      )}
      {actions.canDelete && (
        <Button
          variant={ButtonVariant.TEXT}
          size="no-padding"
          icon={IconChoices.DELETE_TRASH}
          onClick={openDeleteModal}
        />
      )}
      <ConfirmationModal
        title={translate('scheduledPosts.deleteTitle')}
        description={translate('scheduledPosts.deleteText')}
        show={showDeleteModal}
        close={closeDeleteModal}
        onConfirm={() => mutateRemovePost()}
        deleteMode
      />
    </li>
  )
}

export const ScheduledPostsBanner = ({
  userId,
}: {
  userId: string
}): ReactNode => {
  const { translate } = useAppTranslation()
  const [expanded, setExpanded] = useState(true)
  const [editedRow, setEditedRow] = useState<PostRow | null>(null)
  const { data: societies } = useQuery(societyQueries.getUserSocieties(userId))

  const hasBoardOrAdminSociety = (societies ?? []).some(
    (society) =>
      isUserAdminInSociety(society, userId) ||
      isUserBoardMemberInSociety(society, userId)
  )

  const { data: rows } = useQuery({
    ...postRowQueries.scheduled(),
    enabled: hasBoardOrAdminSociety,
  })

  const editor = editedRow && (
    <CreateEditPostModal
      show
      close={() => setEditedRow(null)}
      postId={editedRow.id}
      attachments={editedRow}
    />
  )

  if (!hasBoardOrAdminSociety || !rows?.length) {
    return editor
  }

  const [nextRow] = rows
  const title =
    rows.length === 1
      ? translate('scheduledPosts.bannerTitleSingle')
      : translate('scheduledPosts.bannerTitlePlural', { total: rows.length })

  return (
    <>
      <div className="mb-4 w-full overflow-hidden rounded-2xl border border-blue/40 bg-white">
        <button
          type="button"
          className="flex w-full items-center gap-3 bg-blue/10 px-4 py-3 text-left hover:bg-blue/20"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
            <Icon icon={IconChoices.CLOCK} color={theme.colors.link} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="text-blue" style={theme.textVariants.baseBold}>
              {title}
            </p>
            <p
              className="truncate text-blue/80"
              style={theme.textVariants.caption}
            >
              {translate('scheduledPosts.nextPublish', {
                date: formatDateWithFormat(
                  new Date(nextRow.createdAt),
                  "eee d MMMM 'kl.' HH:mm"
                ),
              })}
            </p>
          </div>
          <Icon
            icon={expanded ? IconChoices.CHEVRON_UP : IconChoices.CHEVRON_DOWN}
            color={theme.colors.link}
          />
        </button>
        {expanded && (
          <ul className="divide-y divide-neutral-90">
            {rows.map((row) => (
              <ScheduledPostItem
                key={row.id}
                row={row}
                onEdit={() => setEditedRow(row)}
              />
            ))}
          </ul>
        )}
      </div>
      {editor}
    </>
  )
}
